import { ChangeDetectionStrategy, Component, computed, DOCUMENT, inject, input, output, Signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { inlineHtml } from '@core/utilities';
import { DEFAULT_POPOVER_CLOSE_LABEL, DEFAULT_POPOVER_PLACEMENT, PopoverPlacement } from './index';

/**
 * The popover surface itself. Created by the `okePopover` directive as the adjacent sibling of the
 * anchor element; not intended to be used directly.
 */
@Component({
  selector: 'div[oke-popover-panel]',
  templateUrl: './popover-panel.html',
  styleUrl: './popover-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    popover: 'auto',
    role: 'note',
    '[id]': '$id()',
    '[attr.data-placement]': '$placement()',
    '[style.position-anchor]': '$anchorName()',
    '(toggle)': 'onToggle($event)'
  }
})
export class PopoverPanel {
  private readonly _document = inject(DOCUMENT);
  private readonly _sanitizer = inject(DomSanitizer);
  /** Referenced by the anchor's `popovertarget` and the close button's `popovertarget`. */
  readonly $id = input.required<string>({ alias: 'id' });
  readonly $content = input.required<string>({ alias: 'content' });
  readonly $placement = input<PopoverPlacement>(DEFAULT_POPOVER_PLACEMENT, { alias: 'placement' });
  readonly $closeLabel = input<string>(DEFAULT_POPOVER_CLOSE_LABEL, { alias: 'closeLabel' });
  readonly $anchorName = input<string | null>(null, { alias: 'anchorName' });
  /** Emits `true` when the panel has opened, `false` when it has closed. */
  readonly toggled = output<boolean>();
  /** Reduced to text plus inline text-level elements, then re-sanitized by Angular on binding. */
  protected readonly $contentHtml: Signal<SafeHtml> = computed(() =>
    this._sanitizer.bypassSecurityTrustHtml(inlineHtml.toInlineHtml(this.$content(), this._document))
  );

  protected onToggle(evt: Event): void {
    this.toggled.emit((evt as ToggleEvent).newState === 'open');
  }
}
